import React from "react";

import ColorPicker from "components/basic/color-picker/ColorPicker";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { Align } from "types/types";
import TimeSpanInputs from "components/fragments/product-template-configurator/ProductTemplateAttributesConfiguration/type-fields/components/TimeSpanInputs";
import { SparkLineProps } from "./SparkLineTypes";

export type SparkLineInputsProps = {
  currentConfig: SparkLineProps;
  onDatasetChange: (
    property: keyof SparkLineProps,
    value: string | number | boolean | null
  ) => void;
  onConfigChange: (
    e:
      | InputNumberValueChangeEvent
      | React.ChangeEvent<HTMLInputElement>
      | InputSwitchChangeEvent
      | DropdownChangeEvent
  ) => void;
  advanced?: boolean;
};

const horizontalAlignOptions: { label: string, value: Align}[] = [
  { label: T("word.left"), value: "left" },
  { label: T("layout.center"), value: "center" },
  { label: T("word.right"), value: "right" },
];

const verticalAlignOptions: { label: string, value: Align}[] = [
  { label: T("word.top"), value: "top" },
  { label: T("layout.center"), value: "center" },
  { label: T("word.bottom"), value: "bottom" },
];

const SparkLineInputs: React.FC<SparkLineInputsProps> = ({
  currentConfig,
  onDatasetChange,
  onConfigChange,
  advanced = false,
}) => {
  return (
    <>
      <div className="col-12 mb-3">
        <label htmlFor="dataset">{T("word.dataset")}</label>
        <DatasetDropdown
          value={currentConfig.dataset}
          onChange={val => onDatasetChange("dataset", val)}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="lineColor">{T("label.line-color")}</label>
        <ColorPicker
          id="lineColor"
          value={currentConfig.lineColor}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="lineWidth">{T("label.line-width")}</label>
        <InputNumber
          id="lineWidth"
          inputClassName="w-full"
          className="p-inputtext-sm"
          suffix=" px"
          value={currentConfig.lineWidth}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="count">{T("action.count")}</label>
        <InputNumber
          id="count"
          inputClassName="w-full"
          className="p-inputtext-sm"
          value={currentConfig.count}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
        <InputNumber
          id="updateFrequency"
          inputClassName="w-full"
          className="p-inputtext-sm"
          value={currentConfig.updateFrequency}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="unit">{T("word.unit")}</label>
        <DatasetUnitSelector
          className="p-inputtext-sm"
          id="unit"
          value={currentConfig.unit}
          onChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label htmlFor="valueScaling">{T("config.spark-line.value-scaling")}</label>
        <InputNumber
          id="valueScaling"
          className="p-inputtext-sm"
          value={currentConfig.valueScaling}
          invalid={currentConfig.valueScaling !== 1}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label htmlFor="decimalPlaces">{T("config.decimal-places")}</label>
        <InputNumber
          id="decimalPlaces"
          className="p-inputtext-sm"
          value={currentConfig.decimalPlaces}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label htmlFor="labelFontSize">{T("config.spark-line.label-font-size")}</label>
        <InputText
          id="labelFontSize"
          type="text"
          className="p-inputtext-sm"
          value={currentConfig.labelFontSize}
          onChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="sparkLineWidth">{T("config.spark-line.spark-line-width")}</label>
        <InputNumber
          id="sparkLineWidth"
          className="p-inputtext-sm"
          value={currentConfig.sparkLineWidth}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label htmlFor="height">{`${T("word.height")} (px)}`}</label>
        <InputNumber
          id="height"
          className="p-inputtext-sm"
          value={currentConfig.height ?? undefined}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label >{`${T("word.max")} ${T("word.age").toLowerCase()}`}</label>
        <div className="p-inputgroup">
          <TimeSpanInputs
            value={currentConfig.maxAge ?? 0}
            onChange={val => onDatasetChange("maxAge", val)}
          />
        </div>
      </div>
      <div className="flex gap-3 mb-3">
        {advanced && (
          <div className="flex flex-column justify-content-between align-items-center px-2">
            <label htmlFor="showYAxis">{T("word.y-axis")}</label>
            <InputSwitch
              id="showYAxis"
              className="p-inputtext-sm mb-2"
              checked={Boolean(currentConfig.showYAxis)}
              onChange={onConfigChange}
            />
          </div>
        )}
        <div className="flex flex-column justify-content-between align-items-center">
          <label htmlFor="overlayLabel">{T("label.spark-line.overlay-label")}</label>
          <InputSwitch
            id="overlayLabel"
            className="p-inputtext-sm mb-2"
            checked={Boolean(currentConfig.overlayLabel)}
            onChange={onConfigChange}
          />
        </div>
        {currentConfig.overlayLabel && (
          <>
            <div className="flex flex-column gap-2">
              <label htmlFor="vertical">{T("label.vertical-align")}</label>
              <Dropdown
                id="vertical"
                className="p-inputtext-sm"
                value={currentConfig.vertical ?? undefined}
                options={verticalAlignOptions}
                onChange={onConfigChange}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="horizontal">{T("label.horizontal-align")}</label>
              <Dropdown
                id="horizontal"
                className="p-inputtext-sm"
                value={currentConfig.horizontal ?? undefined}
                options={horizontalAlignOptions}
                onChange={onConfigChange}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SparkLineInputs;
